import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogId } from "./dialog_id.js";
import { DialogManager } from "./dialog_manager.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { ServerRoutingService } from "../../server_routing/server_routing_service.js";

// Shown when the home server has redirected this client to another server
// after startup already finished: the new address is stored and takes effect
// on the next launch, so the player chooses whether to restart right away.
export class ServerChangedDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.SERVER_CHANGED_DIALOG;

  private static readonly DIALOG_W = 909;
  private static readonly DIALOG_H = 842;

  private bg!: UINineSliceElement;

  constructor()
  {
    const body_w = LayoutScaler.widthFromDesign( ServerChangedDialog.DIALOG_W );
    const body_h = LayoutScaler.heightFromDesign( ServerChangedDialog.DIALOG_H );
    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h, true );

    this.createBackground();
    this.createTitle();
    this.createText();
    this.createButtons();
  }

  public static open(): ServerChangedDialog
  {
    return DialogManager.openPooled( "ServerChangedDialog", undefined, () => new ServerChangedDialog() ) as ServerChangedDialog;
  }

  public override isPoolable(): boolean { return true; }

  public name(): string { return "ServerChangedDialog"; }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  private createBackground(): void
  {
    const w = this.rect.getW();

    this.bg = new UINineSliceElement(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( this.bg );

    const icon_bg_size = LayoutScaler.widthFromDesign( 290 );
    const bg_circle = new UIImage(
      this.ctx,
      w / 2 - icon_bg_size / 2,
      -icon_bg_size / 2 + LayoutScaler.yFromDesign( 22 ),
      icon_bg_size,
      icon_bg_size,
      ImgID.AvatarCircle,
      {},
      false
    );
    this.addChild( bg_circle );

    const icon_size = LayoutScaler.widthFromDesign( 210 );
    bg_circle.addChild( new UIImage(
      this.ctx,
      icon_bg_size / 2 - icon_size / 2,
      LayoutScaler.yFromDesign( 38 ),
      icon_size,
      icon_size,
      ImgID.UI_ConnectionFailedIcon,
      {},
      false
    ) );
  }

  private createTitle(): void
  {
    this.bg.addChild( new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( 136 ),
      this.bg.getRect().getW(),
      LayoutScaler.heightFromDesign( 100 ),
      {
        localization_key: "SERVER_CHANGED.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( 70 ),
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        font_weight: 900,
        auto_wrap: false,
        scale_to_width: true,
        outline_color: ColorScheme.menu_top_el_text,
        outline_width: LayoutScaler.widthFromDesign( 14 ),
      }
    ) );
  }

  private createText(): void
  {
    const bg_w = this.bg.getRect().getW();
    this.bg.addChild( new UITextElement(
      this.ctx,
      bg_w * 0.05,
      LayoutScaler.yFromDesign( 300 ),
      bg_w * 0.9,
      LayoutScaler.heightFromDesign( 250 ),
      {
        localization_key: "SERVER_CHANGED.TEXT",
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        font_weight: 900,
        vertical_align: MyTextVerticalAlign.Top,
        auto_wrap: true,
        outline_color: ColorScheme.menu_top_el_text,
        outline_width: LayoutScaler.widthFromDesign( 10 ),
      }
    ) );
  }

  private createButtons(): void
  {
    const btn_w = LayoutScaler.widthFromDesign( 430 );
    const btn_h = LayoutScaler.heightFromDesign( 240 );
    const gap = LayoutScaler.widthFromDesign( -8 );
    const offset_x = LayoutScaler.xFromDesign( 6 );
    const btn_y = this.bg.getRect().getH() - btn_h - LayoutScaler.yFromDesign( 40 );
    const start_x = ( this.bg.getRect().getW() - ( btn_w * 2 + gap ) ) / 2;

    this.bg.addChild( this.createButton(
      start_x - offset_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_RedBtn,
      "SERVER_CHANGED.LATER",
      () => this.close()
    ) );

    this.bg.addChild( this.createButton(
      start_x + btn_w + gap - offset_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      "SERVER_CHANGED.RESTART",
      () => ServerRoutingService.reload()
    ) );
  }

  private createButton(
    x: number,
    y: number,
    w: number,
    h: number,
    image: ImgID,
    localization_key: string,
    on_click: () => void
  ): UINineSliceElement
  {
    const button = new UINineSliceElement( this.ctx, x, y, w, h, image, "frame_btn", undefined, undefined, true );
    button.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      on_click();
    } );

    const outline_color = image === ImgID.UI_RedBtn ? ColorScheme.red_btn_outline : ColorScheme.more_lives_buy_outline;

    button.addChild( new UITextElement(
      this.ctx, 0, 0, w, h,
      {
        localization_key,
        outline_color,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        font_weight: 800,
        auto_wrap: false,
        scale_to_width: true,
        shadow_blur: LayoutScaler.widthFromDesign( 6 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    ), true );

    return button;
  }
}
